/**
 * Admin — Compliance Dashboard (Sprint 1).
 *
 * Consolidates six compliance-relevant surfaces into one page:
 *
 *   1. Retention policy config (edit `credit_report_retention_days`)
 *   2. Records approaching retention deadline (within 7 days)
 *   3. Recently deleted reports (last 30 days, all 3 sources)
 *   4. Consent status snapshot (last 25 uploads)
 *   5. Audit-log summary (retention window changes + admin per-report
 *      deletions + retention job runs, filtered from admin_audit_logs)
 *   6. Failed retention jobs / cleanup errors (last 10 runs)
 *
 * Composes design-system primitives only — Card, Table, Badge,
 * SemanticStatusBadge, EmptyState, PageHeader, Modal, Button, Input.
 * No banned visuals, no gradients, no sparkles, no emojis in the DOM.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";

import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { PageHeader } from "@/design-system/components/PageHeader";
import { Card, CardHeader, CardBody } from "@/design-system/components/Card";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/Table";
import { Badge } from "@/design-system/components/Badge";
import { SemanticStatusBadge } from "@/design-system/components/SemanticStatusBadge";
import { EmptyState } from "@/design-system/components/EmptyState";
import { Button } from "@/design-system/components/Button";

function formatIso(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)} UTC`;
}

function DeletionSourceBadge({ source }) {
  const map = {
    retention_sweep: { tone: "info", label: "Retention" },
    admin: { tone: "warning", label: "Admin" },
    user_self: { tone: "neutral", label: "User" },
  };
  const cfg = map[source] || { tone: "neutral", label: source || "—" };
  return (
    <Badge tone={cfg.tone} data-testid={`compliance-source-${source || "unknown"}`}>
      {cfg.label}
    </Badge>
  );
}

function JobHealthBadge({ severity, failed }) {
  const isError = severity === "warning" || (failed || 0) > 0;
  return (
    <Badge tone={isError ? "warning" : "success"} data-testid="compliance-job-health-badge">
      {isError ? "Attention" : "Healthy"}
    </Badge>
  );
}

// Retention-policy config form — inline edit with 7..90 validation.
function RetentionConfigCard({ payload, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(payload.retention_days);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setValue(payload.retention_days);
  }, [payload.retention_days]);

  const min = payload.retention_days_min;
  const max = payload.retention_days_max;
  const parsed = Number.parseInt(value, 10);
  const invalid =
    Number.isNaN(parsed) || parsed < min || parsed > max;

  async function save() {
    if (invalid) return;
    setBusy(true);
    setError("");
    try {
      await api.patch("/admin/site-settings", {
        credit_report_retention_days: parsed,
      });
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(formatApiError(e) || "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card data-testid="compliance-retention-config">
      <CardHeader
        title="Retention window"
        description={`Credit-report data is deleted automatically after this many days. Allowed range ${min}–${max}. Changes are written to the admin audit log.`}
        actions={
          !editing ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setEditing(true)}
              data-testid="compliance-retention-edit-btn"
            >
              Edit
            </Button>
          ) : null
        }
      />
      <CardBody>
        {!editing ? (
          <div className="flex items-baseline gap-3">
            <span
              data-testid="compliance-retention-current-value"
              className="text-[32px] font-semibold text-[var(--ds-color-ink)]"
              style={{ fontFamily: "var(--ds-font-display)" }}
            >
              {payload.retention_days}
            </span>
            <span className="text-[13px] text-[var(--ds-color-ink-muted)]">
              days
            </span>
          </div>
        ) : (
          <div className="space-y-3">
            <label
              htmlFor="compliance-retention-input"
              className="block text-[12px] uppercase tracking-wide text-[var(--ds-color-ink-muted)]"
            >
              Retention days
            </label>
            <div className="flex items-center gap-3">
              <input
                id="compliance-retention-input"
                type="number"
                min={min}
                max={max}
                step={1}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid="compliance-retention-input"
                aria-label="Retention days"
                aria-invalid={invalid}
                className="w-28 rounded-[var(--ds-radius-md)] border border-[var(--ds-color-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-[14px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
              />
              <span className="text-[13px] text-[var(--ds-color-ink-muted)]">
                days ({min}–{max})
              </span>
              <Button
                size="sm"
                onClick={save}
                disabled={invalid || busy}
                data-testid="compliance-retention-save-btn"
              >
                {busy ? "Saving…" : "Save"}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setEditing(false);
                  setValue(payload.retention_days);
                  setError("");
                }}
                disabled={busy}
                data-testid="compliance-retention-cancel-btn"
              >
                Cancel
              </Button>
            </div>
            {invalid ? (
              <div
                role="alert"
                data-testid="compliance-retention-invalid"
                className="text-[12px] text-[var(--ds-color-danger)]"
              >
                Enter a whole number between {min} and {max}.
              </div>
            ) : null}
            {error ? (
              <div
                role="alert"
                data-testid="compliance-retention-error"
                className="text-[12px] text-[var(--ds-color-danger)]"
              >
                {error}
              </div>
            ) : null}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function ApproachingDeadlineCard({ rows }) {
  return (
    <Card data-testid="compliance-approaching-deadline">
      <CardHeader
        title="Approaching retention deadline"
        description="Credit reports uploaded 7 or fewer days before the retention cutoff. Contact users if the data must be preserved."
      />
      <CardBody>
        {rows.length === 0 ? (
          <EmptyState
            title="Nothing near expiry"
            description="No credit reports are within 7 days of the retention cutoff."
            data-testid="compliance-approaching-empty"
          />
        ) : (
          <Table data-testid="compliance-approaching-table">
            <THead>
              <TR>
                <TH>User</TH>
                <TH>Bureau</TH>
                <TH>Uploaded</TH>
                <TH>Days remaining</TH>
                <TH>Filename</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TR key={row.report_id} data-testid={`compliance-approaching-row-${row.report_id}`}>
                  <TD className="truncate max-w-[220px]">{row.user_email || row.user_id || "—"}</TD>
                  <TD>{row.bureau || "—"}</TD>
                  <TD>{formatIso(row.created_at)}</TD>
                  <TD>
                    <Badge
                      tone={row.days_remaining <= 2 ? "warning" : "info"}
                      data-testid={`compliance-approaching-days-${row.report_id}`}
                    >
                      {row.days_remaining} days
                    </Badge>
                  </TD>
                  <TD className="truncate max-w-[220px]">{row.original_filename || "—"}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function RecentlyDeletedCard({ rows }) {
  return (
    <Card data-testid="compliance-recently-deleted">
      <CardHeader
        title="Recently deleted reports"
        description="Credit-report deletions in the last 30 days across the retention job, admin actions, and user self-deletion."
      />
      <CardBody>
        {rows.length === 0 ? (
          <EmptyState
            title="No recent deletions"
            description="No credit-report deletions have been recorded in the past 30 days."
            data-testid="compliance-recently-deleted-empty"
          />
        ) : (
          <Table data-testid="compliance-recently-deleted-table">
            <THead>
              <TR>
                <TH>Source</TH>
                <TH>Deleted at</TH>
                <TH>User</TH>
                <TH>Bureau</TH>
                <TH>Filename</TH>
                <TH>Uploaded</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TR key={row.id} data-testid={`compliance-recently-deleted-row-${row.id}`}>
                  <TD><DeletionSourceBadge source={row.source} /></TD>
                  <TD>{formatIso(row.occurred_at)}</TD>
                  <TD className="truncate max-w-[200px]">{row.admin_email || row.user_id || "—"}</TD>
                  <TD>{row.report_bureau || "—"}</TD>
                  <TD className="truncate max-w-[200px]">{row.report_original_filename || "—"}</TD>
                  <TD>{formatIso(row.report_created_at)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function ConsentSnapshotCard({ rows }) {
  const missing = rows.filter((r) => !r.consent_acknowledged_at).length;
  return (
    <Card data-testid="compliance-consent-snapshot">
      <CardHeader
        title="Consent snapshot"
        description={`Latest 25 uploads and the consent timestamp captured at upload time. ${missing} missing consent record${missing === 1 ? "" : "s"}.`}
      />
      <CardBody>
        {rows.length === 0 ? (
          <EmptyState
            title="No recent uploads"
            description="No credit reports have been uploaded yet."
            data-testid="compliance-consent-empty"
          />
        ) : (
          <Table data-testid="compliance-consent-table">
            <THead>
              <TR>
                <TH>User</TH>
                <TH>Uploaded</TH>
                <TH>Consent</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TR key={row.report_id} data-testid={`compliance-consent-row-${row.report_id}`}>
                  <TD className="truncate max-w-[240px]">{row.user_email || row.user_id || "—"}</TD>
                  <TD>{formatIso(row.created_at)}</TD>
                  <TD>
                    {row.consent_acknowledged_at ? (
                      <SemanticStatusBadge
                        emoji="✅"
                        label={`Consent ${formatIso(row.consent_acknowledged_at)}`}
                        data-testid={`compliance-consent-ok-${row.report_id}`}
                      />
                    ) : (
                      <SemanticStatusBadge
                        emoji="⚠️"
                        label="Consent missing"
                        data-testid={`compliance-consent-missing-${row.report_id}`}
                      />
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function AuditLogCard({ rows }) {
  return (
    <Card data-testid="compliance-audit-summary">
      <CardHeader
        title="Retention-related audit log"
        description="Retention window edits, retention job runs, and admin per-report deletions in the last 30 days."
      />
      <CardBody>
        {rows.length === 0 ? (
          <EmptyState
            title="No entries yet"
            description="No retention-related admin activity has been recorded."
            data-testid="compliance-audit-empty"
          />
        ) : (
          <Table data-testid="compliance-audit-table">
            <THead>
              <TR>
                <TH>When</TH>
                <TH>Actor</TH>
                <TH>Action</TH>
                <TH>Target</TH>
                <TH>Detail</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TR key={row.id} data-testid={`compliance-audit-row-${row.id}`}>
                  <TD>{formatIso(row.created_at)}</TD>
                  <TD className="truncate max-w-[200px]">{row.actor_email || "—"}</TD>
                  <TD>
                    <Badge
                      tone={row.severity === "warning" ? "warning" : "neutral"}
                      data-testid={`compliance-audit-action-${row.id}`}
                    >
                      {row.action_type}
                    </Badge>
                  </TD>
                  <TD className="truncate max-w-[200px]">{row.target_label || "—"}</TD>
                  <TD className="text-[12px] text-[var(--ds-color-ink-muted)] truncate max-w-[240px]">
                    {row.changes ? JSON.stringify(row.changes) : "—"}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function JobHealthCard({ rows }) {
  const hasFailure = rows.some((r) => (r.failed_count || 0) > 0);
  return (
    <Card data-testid="compliance-job-health">
      <CardHeader
        title="Retention job health"
        description="Most recent retention sweep runs and their outcomes."
        actions={hasFailure ? (
          <span
            role="status"
            data-testid="compliance-job-alert"
            className="inline-flex items-center gap-1 text-[12px] text-[var(--ds-color-warning)]"
          >
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            Attention needed
          </span>
        ) : null}
      />
      <CardBody>
        {rows.length === 0 ? (
          <EmptyState
            title="No runs yet"
            description="The retention scheduler has not yet reported a run."
            data-testid="compliance-job-health-empty"
          />
        ) : (
          <Table data-testid="compliance-job-health-table">
            <THead>
              <TR>
                <TH>Ran at</TH>
                <TH>Status</TH>
                <TH>Picked</TH>
                <TH>Deleted</TH>
                <TH>Failed</TH>
                <TH>Duration</TH>
                <TH>Errors</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TR key={row.id} data-testid={`compliance-job-row-${row.id}`}>
                  <TD>{formatIso(row.run_at)}</TD>
                  <TD>
                    <JobHealthBadge severity={row.severity} failed={row.failed_count} />
                  </TD>
                  <TD>{row.picked ?? 0}</TD>
                  <TD>{row.rows_deleted ?? 0}</TD>
                  <TD>{row.failed_count ?? 0}</TD>
                  <TD>{row.duration_ms != null ? `${row.duration_ms}ms` : "—"}</TD>
                  <TD className="text-[12px] text-[var(--ds-color-ink-muted)] truncate max-w-[280px]">
                    {(row.errors_sample || []).length
                      ? (row.errors_sample || []).join(" | ")
                      : "—"}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

export default function AdminCompliance() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/compliance/dashboard");
      setPayload(data);
      setRefreshedAt(new Date());
    } catch (e) {
      setError(formatApiError(e) || "Failed to load compliance dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const subtitle = useMemo(() => {
    if (!refreshedAt) return "Loading…";
    return `Refreshed ${refreshedAt.toISOString().slice(11, 19)} UTC`;
  }, [refreshedAt]);

  return (
    <AdminLayout title="Compliance" subtitle={subtitle}>
      <AdminPageHelp
        title="Compliance Dashboard"
        purpose="Central visibility into credit-report retention and per-record deletion history."
        configures="Edit the retention window (7–90 days) inline, review records approaching expiry, and audit deletions across the retention scheduler, admin actions, and user self-service."
        impact="Retention-window changes take effect on the next scheduler tick (daily). Every change and every job run is written to the admin audit log."
        safety="Retention changes are audited. Individual reports can also be deleted from the User drawer, and every deletion is captured in this dashboard."
      />
      <PageHeader
        eyebrow="Compliance"
        title="Retention & audit"
        description="Configurable retention automation and a consolidated view of deletion history + consent capture."
        data-testid="compliance-page-header"
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="compliance-refresh-btn"
          >
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden />
            <span>{loading ? "Loading…" : "Refresh"}</span>
          </Button>
        }
      />

      {error ? (
        <div
          role="alert"
          data-testid="compliance-load-error"
          className="mb-4 text-[13px] text-[var(--ds-color-danger)]"
        >
          {error}
        </div>
      ) : null}

      {loading && !payload ? (
        <div
          role="status"
          data-testid="compliance-loading"
          className="text-[13px] text-[var(--ds-color-ink-muted)]"
        >
          Loading compliance dashboard…
        </div>
      ) : null}

      {payload ? (
        <div className="grid gap-6">
          <RetentionConfigCard payload={payload} onSaved={load} />
          <ApproachingDeadlineCard rows={payload.approaching_deadline || []} />
          <RecentlyDeletedCard rows={payload.recently_deleted || []} />
          <ConsentSnapshotCard rows={payload.consent_snapshot || []} />
          <AuditLogCard rows={payload.audit_log_summary || []} />
          <JobHealthCard rows={payload.job_health || []} />
        </div>
      ) : null}
    </AdminLayout>
  );
}

// Named subcomponent exports for testing.
export {
  RetentionConfigCard,
  ApproachingDeadlineCard,
  RecentlyDeletedCard,
  ConsentSnapshotCard,
  AuditLogCard,
  JobHealthCard,
};
